import { Ban, Download, Edit3, ExternalLink, Eye, Link2, RefreshCw, Sparkles, Trash2 } from "lucide-react";
import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import type { Mandate } from "@/types/mandate";

interface MenuItemProps {
  icon: ReactNode;
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  highlight?: boolean;
}

function MenuItem({ icon, children, onClick, disabled, danger, highlight }: MenuItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer ${
        danger ? "text-danger" : highlight ? "font-medium text-primary-600 hover:bg-primary-50/50" : "text-ink"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

interface MandateMenuProps {
  row: Mandate;
  refreshing: boolean;
  onView: () => void;
  onCopy: () => void;
  onPreview: () => void;
  onRefresh: () => void;
  onDownload: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onGenerateLink?: () => void;
  onEditDraft?: () => void;
}

export function MandateMenu(p: MandateMenuProps) {
  const isDraft = p.row.status === "DRAFT";
  const resendable = p.row.status === "PENDING_AUTH" || p.row.status === "CREATED";
  const cancelable = p.row.status === "ACTIVE" || p.row.status === "ACTIVATED";
  const downloadable =
    p.row.status === "ACTIVE" ||
    p.row.status === "ACTIVATED" ||
    p.row.status === "PAUSED" ||
    p.row.status === "COMPLETED";
  const deletable = p.row.status === "CREATED" || isDraft;

  return (
    <>
      {isDraft && p.onGenerateLink && (
        <MenuItem
          highlight
          icon={<Sparkles className="h-4 w-4 text-primary-500" />}
          onClick={p.onGenerateLink}
        >
          Generate Mandate Link
        </MenuItem>
      )}
      {isDraft && p.onEditDraft && (
        <MenuItem icon={<Edit3 className="h-4 w-4 text-ink-muted" />} onClick={p.onEditDraft}>
          Edit Draft
        </MenuItem>
      )}
      <MenuItem icon={<Eye className="h-4 w-4 text-ink-muted" />} onClick={p.onView}>
        View Details
      </MenuItem>
      {resendable && (
        <MenuItem icon={<Link2 className="h-4 w-4 text-ink-muted" />} onClick={p.onCopy}>
          Copy Link
        </MenuItem>
      )}
      {resendable && (
        <MenuItem icon={<ExternalLink className="h-4 w-4 text-ink-muted" />} onClick={p.onPreview}>
          Preview Link
        </MenuItem>
      )}
      {!isDraft && (
        <MenuItem
          icon={
            p.refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin text-ink-muted" />
            ) : (
              <RefreshCw className="h-4 w-4 text-ink-muted" />
            )
          }
          onClick={p.onRefresh}
        >
          {p.refreshing ? "Refreshing..." : "Refresh Status"}
        </MenuItem>
      )}
      {downloadable && (
        <MenuItem icon={<Download className="h-4 w-4 text-ink-muted" />} onClick={p.onDownload}>
          Download Receipt
        </MenuItem>
      )}
      {cancelable && (
        <MenuItem danger icon={<Ban className="h-4 w-4" />} onClick={p.onCancel}>
          Cancel Mandate
        </MenuItem>
      )}
      {deletable && (
        <MenuItem danger icon={<Trash2 className="h-4 w-4" />} onClick={p.onDelete}>
          {isDraft ? "Delete Draft" : "Delete Mandate"}
        </MenuItem>
      )}
    </>
  );
}